import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount, StatCard, ListPagination } from '@redpanda/ui';
import { isAdmin } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { useProjectTimeEntriesCache, useProfitabilityCache } from '../stores/timeEntryStore';
import { useMembers, memberName } from '../stores/lookupStore';
import { useDataChanged } from '../utils/dataEvents';
import { ProjectProfitability } from '../types/pm';
import TimeEntryTable, { formatHours } from './TimeEntryTable';

/**
 * 專案毛利（管理員）：工時異動、收款／分包異動（合約金額）時重抓
 * 專案沒有記錄任何工時、外部成本也沒設成本預算時回傳 null：不記工時的團隊不會看到「人力成本 $0、毛利率 100%」這種沒有意義的數字，開始記工時就自動出現
 */
export const useProfitability = (projectId: string) => {
  const { user } = useContext(UserContext)!;
  const admin = isAdmin(user?.role);
  // 有快取：再次進來先顯示上次的數字，卡片不會晚一步才跳出來
  const { data, refetch, key } = useProfitabilityCache(admin && projectId ? projectId : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, [key]);
  useDataChanged(['timeEntries', 'packages', 'projects', 'expenses'], refetch);
  // 有工時、外部成本或設了成本預算才顯示（舊快取可能沒有新欄位）
  return admin && data && (data.hours > 0 || (data.expenseCost ?? 0) > 0 || data.costBudget) ? data : null;
};

/** 毛利卡片（管理員）：人力成本、毛利、毛利率、工時（已記錄／預估） */
const ProfitabilityCards: React.FC<{ data: ProjectProfitability }> = ({ data }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const members = useMembers();
  const negative = data.margin < 0;
  const top = data.members.slice(0, 2).map(m => `${memberName(members, m.userId)} ${formatHours(m.hours)}h`).join('、');
  return (
    <div className="mb-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard title={t('pmTime.profit.laborCost', { ns: 'pages' })} value={<Amount value={data.laborCost} />} hint={top || undefined} />
      <StatCard
        title={t('pmTime.profit.margin', { ns: 'pages' })}
        value={<span className={negative ? 'text-red-600 dark:text-red-400' : ''}><Amount value={data.margin} /></span>}
        hint={t('pmTime.profit.marginHint', { ns: 'pages' })}
      />
      <StatCard
        title={t('pmTime.profit.marginRate', { ns: 'pages' })}
        value={<span className={negative ? 'text-red-600 dark:text-red-400' : ''}>{data.marginRate === null ? '—' : `${data.marginRate}%`}</span>}
      />
      <StatCard
        title={t('pmTime.profit.hours', { ns: 'pages' })}
        value={<>{formatHours(data.hours)}<span className="ml-1 text-base font-normal text-fg-secondary">/ {formatHours(data.estimatedHours)}</span></>}
        hint={data.unratedHours > 0 ? <span className="text-amber-700 dark:text-amber-400">{t('pmTime.profit.unrated', { ns: 'pages', hours: formatHours(data.unratedHours) })}</span> : undefined}
      />
    </div>
  );
};

/** 專案詳情「工時」分頁：記錄分頁顯示（長期專案的記錄很多） */
const ProjectTimePanel: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const [page, setPage] = useState(1);
  const { data, refetch, key } = useProjectTimeEntriesCache({ projectId, page });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refetch(); }, [key]);
  useDataChanged('timeEntries', refetch);
  const profitability = useProfitability(projectId);

  return (
    <>
      {profitability && <ProfitabilityCards data={profitability} />}
      <ListPagination pagination={data?.pagination} onPageChange={setPage} className="mb-3" />
      <TimeEntryTable
        entries={data?.data}
        showProject={false}
        showMember
        emptyTitle={t('pmTime.emptyProject', { ns: 'pages' })}
        emptyHint={t('pmTime.emptyHint', { ns: 'pages' })}
      />
      <ListPagination pagination={data?.pagination} onPageChange={setPage} className="mt-6" />
    </>
  );
};

export default ProjectTimePanel;
